import { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box, Typography, TextField, InputAdornment, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    ToggleButtonGroup, ToggleButton, LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../../context/userContext';
import { getAllTutorials, deleteTutorial, getCategoryCovers, setCategoryCover, uploadTutorialImage } from '../../api/tutorialApi';
import { normaliseTutorial, groupItems } from './learningTypes';
import YouTubeEmbed from './YouTubeEmbed';
import Highlight from './Highlight';

const PP = "'Poppins', sans-serif";
const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

// ── Category / Sub-Category translation map ────────────────────────────────
const TUT_LABELS = {
    '入门与账户': { en: 'Getting Started & Account', my: 'Permulaan & Akaun' },
    '计算器': { en: 'Calculators', my: 'Kalkulator' },
    '价格与工具': { en: 'Pricing & Tools', my: 'Harga & Alatan' },
    'ATAP 与太阳能介绍': { en: 'Introduction to ATAP & Solar', my: 'Pengenalan ATAP & Solar' },
    'AI BESS 360 太阳能系统': { en: 'AI BESS 360 Solar System', my: 'Sistem Solar AI BESS 360' },
    '产品与配件': { en: 'Product & Add-on', my: 'Produk & Tambahan' },
    '促销与当局回扣': { en: 'Promotion & Authority Rebate', my: 'Promosi & Rebat Pihak Berkuasa' },
    'ATAP 定价': { en: 'ATAP Pricing', my: 'Harga ATAP' },
    '配件': { en: 'Add-ons', my: 'Tambahan' },
    '销售': { en: 'Sales', my: 'Jualan' },
    '收入': { en: 'Earnings', my: 'Pendapatan' },
    '资源': { en: 'Resources', my: 'Sumber' },
    '登录与导览': { en: 'Login & Navigation', my: 'Log Masuk & Navigasi' },
    '安装到手机': { en: 'Install on Phone', my: 'Pasang di Telefon' },
    '个人资料': { en: 'Profile', my: 'Profil' },
    'RSC 住宅太阳能计算器': { en: 'RSC Residential Calculator', my: 'Kalkulator RSC' },
    '设计申请': { en: 'Design Request', my: 'Permohonan Reka Bentuk' },
    '创建销售': { en: 'Create Sale', my: 'Cipta Jualan' },
    '佣金': { en: 'Commission', my: 'Komisen' },
    '奖励': { en: 'Incentives', my: 'Insentif' },
    '仪表板': { en: 'Dashboard', my: 'Papan Pemuka' },
    '学习中心': { en: 'Learning Center', my: 'Pusat Pembelajaran' },
};

const NO_VIDEO_TEXT = {
    cn: '暂无该语言视频',
    en: 'No video for this language yet',
    my: 'Tiada video untuk bahasa ini buat masa ini',
};

// Progress label per language
const PROGRESS_LABEL = {
    cn: '进度',
    en: 'Progress',
    my: 'Kemajuan',
};

// Mark-as-done button labels per language
const MARK_DONE_LABELS = {
    cn: { done: '已完成', pending: '标记完成' },
    en: { done: 'Done', pending: 'Mark as done' },
    my: { done: 'Siap', pending: 'Tandai Selesai' },
};

function resolveImageUrl(url) {
    if (!url) return url;
    return url.startsWith('/') ? `${API_BASE}${url}` : url;
}

function parseAttachments(raw) {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((a) => a && a.url) : [];
    } catch {
        return [];
    }
}

// ── Parse videoLinks JSON into { cn, en, my } arrays ──────────────────────
// Tolerates: object with keys, plain array, or comma/newline-separated string.
function parseVideoLinks(raw) {
    if (!raw) return { cn: [], en: [], my: [] };
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            // New object shape
            const toArr = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
            return {
                cn: toArr(parsed.cn),
                en: toArr(parsed.en),
                my: toArr(parsed.my),
            };
        }
        if (Array.isArray(parsed)) {
            // Legacy plain array → treat as cn
            return { cn: parsed.filter(Boolean), en: [], my: [] };
        }
        // Unexpected object fallback
        return { cn: [], en: [], my: [] };
    } catch {
        // Legacy comma/newline-separated string → treat as cn
        const lines = String(raw).split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
        return { cn: lines, en: [], my: [] };
    }
}

function flattenTopics(groups) {
    const flat = [];
    for (const g of groups) {
        for (const item of g.items) {
            flat.push({ ...item, _category: g.category, _subCategory: null });
        }
        for (const sg of g.subGroups) {
            for (const item of sg.items) {
                flat.push({ ...item, _category: g.category, _subCategory: sg.subCategory });
            }
        }
    }
    return flat;
}

export default function TutorialsReader() {
    const { user } = useUser();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [tutorials, setTutorials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [currentIdx, setCurrentIdx] = useState(0);
    const [done, setDone] = useState(false);
    const [viewLang, setViewLang] = useState('en');

    // Accordion state: which category heading is currently expanded
    const [expandedCategory, setExpandedCategory] = useState(null);

    // Cover-landing view: { category, subCategory } or null
    const [coverView, setCoverView] = useState(null);

    // Delete confirmation dialog
    const [confirmDelete, setConfirmDelete] = useState(null);

    // Per-category (topic) cover photos: { [category]: coverUrl }
    const [categoryCovers, setCategoryCovers] = useState({});

    // Edit-cover dialog: { category } or null
    const [coverEdit, setCoverEdit] = useState(null);

    // ── Per-lesson progress (localStorage) ────────────────────────────────
    const userId = user?.id || user?.userId || null;
    const storageKey = userId ? `tutorial_done_${userId}` : 'tutorial_done_anon';

    const [doneSet, setDoneSet] = useState(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) return new Set(arr.map(String));
            }
        } catch { /* ignore */ }
        return new Set();
    });

    // Persist doneSet whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify([...doneSet]));
        } catch { /* ignore */ }
    }, [doneSet, storageKey]);

    const toggleDone = (lessonId) => {
        const key = String(lessonId);
        setDoneSet((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // ── ?id= query param: jump to the lesson once flat is ready ────────────
    const idParam = searchParams.get('id');
    const idJumpedRef = useRef(false);

    const canManage = user?.roleRights?.includes('ManageFaq')
        || user?.roles?.some((r) => ['Admin', 'Super Admin'].includes(r));

    // Helper: pick localised field with fallback to Chinese
    const pick = (item, field) => {
        if (viewLang === 'en') {
            const enField = field + 'En';
            return (item[enField] && item[enField].trim()) ? item[enField] : item[field];
        }
        if (viewLang === 'my') {
            const myField = field + 'My';
            return (item[myField] && item[myField].trim()) ? item[myField] : item[field];
        }
        return item[field] || '';
    };

    // Helper: translate a category/subCategory label
    const tLabel = (value) => {
        if (!value) return value;
        const entry = TUT_LABELS[value];
        if (!entry) return value;
        if (viewLang === 'en') return entry.en || value;
        if (viewLang === 'my') return entry.my || value;
        return value;
    };

    const fetchTutorials = async () => {
        try {
            const r = await getAllTutorials();
            setTutorials(r.data || []);
        } catch { setTutorials([]); }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchTutorials(); }, []);

    const reloadCategoryCovers = async () => {
        try {
            const r = await getCategoryCovers();
            const map = {};
            (r.data || []).forEach((c) => { if (c && c.category) map[c.category] = c.coverUrl || null; });
            setCategoryCovers(map);
        } catch { setCategoryCovers({}); }
    };
    useEffect(() => { reloadCategoryCovers(); }, []);

    const groups = useMemo(() => {
        const items = tutorials.map(normaliseTutorial);
        const q = search.trim().toLowerCase();
        // Search across all 3 languages (CN/EN/BM) of title + description AND the
        // translated category / sub-category labels, so e.g. "sales" finds 销售.
        const lbl = (v) => { const e = TUT_LABELS[v]; return e ? `${e.en} ${e.my}` : ''; };
        const blob = (it) => [
            it.title, it.titleEn, it.titleMy,
            it.description, it.descriptionEn, it.descriptionMy,
            it.category, lbl(it.category),
            it.subCategory, lbl(it.subCategory),
        ].filter(Boolean).join(' ').toLowerCase();
        const filtered = q ? items.filter((it) => blob(it).includes(q)) : items;
        return groupItems(filtered);
    }, [tutorials, search]);

    // Extend each flat item with the raw tutorial data for localised fields
    const flat = useMemo(() => {
        const base = flattenTopics(groups);
        return base.map((item) => {
            const raw = tutorials.find((t) => t.tutorialId === item.id);
            return raw ? {
                ...item,
                titleEn: raw.titleEn || '',
                titleMy: raw.titleMy || '',
                descriptionEn: raw.descriptionEn || '',
                descriptionMy: raw.descriptionMy || '',
                videoLinksRaw: raw.videoLinks || '',
                coverUrl: raw.coverUrl || null,
                attachments: raw.attachments || null,
            } : { ...item, titleEn: '', titleMy: '', descriptionEn: '', descriptionMy: '', videoLinksRaw: '', coverUrl: null, attachments: null };
        });
    }, [groups, tutorials]);

    // Reset index when search changes or tutorials first load (not on every flat recalc)
    useEffect(() => {
        setCurrentIdx(0);
        setDone(false);
        idJumpedRef.current = false; // allow re-jump if search is cleared
    }, [search, tutorials.length]);

    // Default: expand the category of the first item; when search changes, re-sync
    useEffect(() => {
        if (flat.length > 0) {
            setExpandedCategory(flat[0]._category || null);
        }
    }, [search, tutorials.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Jump to ?id= lesson once flat is populated, only once per page load
    useEffect(() => {
        if (!idParam || idJumpedRef.current || flat.length === 0) return;
        const idx = flat.findIndex((f) => String(f.id) === String(idParam));
        if (idx !== -1) {
            setCurrentIdx(idx);
            setDone(false);
            setCoverView(null);
            setExpandedCategory(flat[idx]._category || null);
        }
        idJumpedRef.current = true;
    }, [flat, idParam]);

    const current = flat[currentIdx];
    const isLast = currentIdx === flat.length - 1;
    const isFirst = currentIdx === 0;

    const handleNext = () => {
        if (!isLast) { setCoverView(null); setCurrentIdx((i) => i + 1); }
    };
    const handlePrev = () => {
        if (done) setDone(false);
        setCoverView(null);
        setCurrentIdx((i) => Math.max(0, i - 1));
    };
    const jumpTo = (id, category) => {
        const idx = flat.findIndex((t) => t.id === id);
        if (idx !== -1) {
            setCurrentIdx(idx);
            setDone(false);
            setCoverView(null);
            if (category) setExpandedCategory(category);
        }
    };

    // ── Admin handlers ───────────────────────────────────────────
    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await deleteTutorial(confirmDelete.id);
            setConfirmDelete(null);
            fetchTutorials();
        } catch {
            alert('Failed to delete');
        }
    };

    if (loading) return <Box sx={{ p: 3, fontFamily: PP }}>Loading...</Box>;

    // Per-language videos for the current item
    const currentVideos = current ? parseVideoLinks(current.videoLinksRaw) : { cn: [], en: [], my: [] };
    const videosForLang = currentVideos[viewLang] || [];
    const fallbackVideos = viewLang !== 'cn' && videosForLang.length === 0 ? currentVideos.cn : [];

    // Progress counts
    const doneCount = flat.filter((f) => doneSet.has(String(f.id))).length;
    const progressPct = flat.length > 0 ? (doneCount / flat.length) * 100 : 0;

    // Current lesson done state
    const currentIsDone = current ? doneSet.has(String(current.id)) : false;
    const markLabels = MARK_DONE_LABELS[viewLang] || MARK_DONE_LABELS.cn;

    return (
        <Box sx={{ fontFamily: PP, width: '100%', pb: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
                        Tutorials
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: PP }}>
                        Video walkthroughs · {flat.length} topics
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {/* Language toggle */}
                    <ToggleButtonGroup
                        value={viewLang}
                        exclusive
                        onChange={(_, v) => { if (v) setViewLang(v); }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                fontFamily: PP,
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                px: 1.5,
                                py: 0.4,
                                border: '1px solid #E2E8F0',
                                color: '#64748B',
                                '&.Mui-selected': {
                                    color: '#0F62CC',
                                    bgcolor: '#E0EFFF',
                                    borderColor: '#1A8CFF',
                                },
                            },
                        }}
                    >
                        <ToggleButton value="en">EN</ToggleButton>
                        <ToggleButton value="cn">中文</ToggleButton>
                        <ToggleButton value="my">BM</ToggleButton>
                    </ToggleButtonGroup>
                    {canManage && (
                        <Button
                            variant="contained" size="small" startIcon={<AddIcon />}
                            onClick={() => navigate('/tutorials/new')}
                            sx={{
                                fontFamily: PP, fontSize: '0.8rem', fontWeight: 600,
                                textTransform: 'none', borderRadius: '8px',
                                background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)',
                                boxShadow: 'none',
                            }}
                        >
                            Add Tutorial
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Progress bar */}
            {flat.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>
                            {PROGRESS_LABEL[viewLang] || PROGRESS_LABEL.cn}: {doneCount} / {flat.length}
                        </Typography>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8' }}>
                            {Math.round(progressPct)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progressPct}
                        sx={{
                            height: 6,
                            borderRadius: 4,
                            bgcolor: '#E2E8F0',
                            '& .MuiLinearProgress-bar': { bgcolor: '#16A34A', borderRadius: 4 },
                        }}
                    />
                </Box>
            )}

            {/* Search */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth size="small" placeholder="Search topics..."
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP, fontSize: '0.85rem' } }}
                />
            </Box>

            {flat.length === 0 ? (
                <Box sx={{ fontFamily: PP, maxWidth: 900, mx: 'auto', py: 6, textAlign: 'center' }}>
                    <MenuBookIcon sx={{ fontSize: 56, color: '#CBD5E1', mb: 1 }} />
                    <Typography sx={{ fontFamily: PP, fontSize: '1.1rem', fontWeight: 600, color: '#0F172A' }}>
                        No tutorials yet
                    </Typography>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.85rem', color: '#64748B', mt: 1 }}>
                        {search ? `No matches for "${search}". Try clearing the search.` : (canManage ? 'Click "Add Tutorial" to create the first one.' : 'Tutorials will appear here once your admin adds them.')}
                    </Typography>
                    {search && (
                        <Button onClick={() => setSearch('')} sx={{ mt: 2, fontFamily: PP, textTransform: 'none' }}>
                            Clear search
                        </Button>
                    )}
                </Box>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 1fr' }, gap: 2.5, alignItems: 'flex-start' }}>
                    {/* ToC — accordion: only the active category is expanded */}
                    <Box
                        sx={{
                            bgcolor: '#FFFFFF',
                            border: '1px solid #E2E8F0',
                            borderRadius: '12px',
                            boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
                            p: 1.75,
                            position: { md: 'sticky' }, top: { md: 16 },
                            maxHeight: { md: 'calc(100vh - 100px)' }, overflowY: 'auto',
                        }}
                    >
                        {groups.map(({ category, items, subGroups }, topicIdx) => {
                            const topicNum = topicIdx + 1;
                            const isExpanded = expandedCategory === category;
                            const hasActiveItem = current && !done && (
                                items.some((it) => it.id === current.id) ||
                                subGroups.some((sg) => sg.items.some((it) => it.id === current.id))
                            );
                            const handleCategoryClick = () => {
                                if (isExpanded) {
                                    // Already open — just show the category cover landing
                                    setCoverView({ category, subCategory: null });
                                    return;
                                }
                                // Expand AND show the category cover landing
                                setExpandedCategory(category);
                                setCoverView({ category, subCategory: null });
                            };
                            const catCover = categoryCovers[category] || null;
                            return (
                                <Box key={category} sx={{ mb: 0.5 }}>
                                    {/* Category header — clickable, shows expand/collapse icon */}
                                    <Box
                                        onClick={handleCategoryClick}
                                        sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            px: 1.25, py: 1, borderRadius: '8px',
                                            bgcolor: isExpanded ? '#F0F7FF' : (hasActiveItem ? '#F8FAFC' : 'transparent'),
                                            '&:hover': { bgcolor: isExpanded ? '#F0F7FF' : '#F8FAFC' },
                                            mb: isExpanded ? 0.5 : 0,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                            {catCover && (
                                                <img
                                                    src={resolveImageUrl(catCover)}
                                                    alt=""
                                                    style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', flexShrink: 0, border: '1px solid #E2E8F0' }}
                                                />
                                            )}
                                            <Typography
                                                sx={{
                                                    fontFamily: PP, fontSize: '0.9rem', fontWeight: 700,
                                                    color: isExpanded ? '#0F62CC' : '#0F172A',
                                                    lineHeight: 1.35,
                                                }}
                                            >
                                                {topicNum}. {tLabel(category)}
                                            </Typography>
                                        </Box>
                                        {isExpanded
                                            ? <ExpandLessIcon sx={{ fontSize: 16, color: '#0F62CC', flexShrink: 0 }} />
                                            : <ExpandMoreIcon sx={{ fontSize: 16, color: '#94A3B8', flexShrink: 0 }} />
                                        }
                                    </Box>

                                    {/* Expanded content */}
                                    {isExpanded && (
                                        <Box>
                                            {/* Direct items (no subtopic) */}
                                            {items.map((it, idx) => {
                                                const flatItem = flat.find((f) => f.id === it.id) || it;
                                                return (
                                                    <TocRow
                                                        key={`${it.source}-${it.id}`}
                                                        item={flatItem}
                                                        number={`${topicNum}.${idx + 1}`}
                                                        active={current?.id === it.id && !done}
                                                        isDone={doneSet.has(String(it.id))}
                                                        onClick={() => jumpTo(it.id, category)}
                                                        query={search}
                                                        pickTitle={(item) => pick(item, 'title')}
                                                    />
                                                );
                                            })}
                                            {subGroups.map(({ subCategory, items: subItems }, subIdx) => {
                                                const subNum = `${topicNum}.${items.length + subIdx + 1}`;
                                                return (
                                                    <Box key={subCategory} sx={{ mt: 0.5 }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex', alignItems: 'center', gap: 0.75,
                                                                pl: 2, mt: 0.75, mb: 0.4, py: 0.4,
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{ fontFamily: PP, fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}
                                                            >
                                                                {subNum} {tLabel(subCategory)}
                                                            </Typography>
                                                        </Box>
                                                        {subItems.map((it, idx) => {
                                                            const flatItem = flat.find((f) => f.id === it.id) || it;
                                                            return (
                                                                <TocRow
                                                                    key={`${it.source}-${it.id}`}
                                                                    item={flatItem}
                                                                    number={`${subNum}.${idx + 1}`}
                                                                    indent
                                                                    active={current?.id === it.id && !done}
                                                                    isDone={doneSet.has(String(it.id))}
                                                                    onClick={() => jumpTo(it.id, category)}
                                                                    query={search}
                                                                    pickTitle={(item) => pick(item, 'title')}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Topic content */}
                    <Box
                        sx={{
                            bgcolor: '#FFFFFF',
                            border: '1px solid #E2E8F0',
                            borderRadius: '12px',
                            boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
                            p: { xs: 2, md: 3 }, minHeight: 400,
                        }}
                    >
                        {coverView ? (
                            <CoverLandingPanel
                                coverUrl={categoryCovers[coverView.category] || null}
                                label={tLabel(coverView.subCategory || coverView.category)}
                                viewLang={viewLang}
                                canManage={canManage}
                                onEditCover={() => setCoverEdit({ category: coverView.category })}
                            />
                        ) : done ? (
                            <DonePanel total={flat.length} onRestart={() => { setCurrentIdx(0); setDone(false); }} />
                        ) : (
                            <>
                                {/* Lesson content has no cover banner — covers live only on the
                                    topic landing (1st layer). Lessons (3rd layer) show content only. */}

                                {/* Breadcrumb + admin actions */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography
                                        sx={{ fontFamily: PP, fontSize: '0.7rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                    >
                                        {tLabel(current._category)}{current._subCategory ? ` · ${tLabel(current._subCategory)}` : ''}
                                        <Typography component="span" sx={{ ml: 1, color: '#CBD5E1', fontWeight: 400 }}>
                                            {currentIdx + 1} / {flat.length}
                                        </Typography>
                                    </Typography>
                                    {canManage && (
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => current && navigate(`/tutorials/${current.id}/edit`)}
                                                title="Edit this tutorial"
                                            >
                                                <EditIcon sx={{ fontSize: 18, color: '#64748B' }} />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => setConfirmDelete(current)} title="Delete this tutorial">
                                                <DeleteIcon sx={{ fontSize: 18, color: '#EF4444' }} />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>

                                <Typography
                                    sx={{ fontFamily: PP, fontSize: '1.4rem', fontWeight: 800, color: '#0F172A', mb: 2, lineHeight: 1.3 }}
                                >
                                    <Highlight text={pick(current, 'title')} query={search} />
                                </Typography>

                                {/* Per-language video section — shown FIRST, above the text content. */}
                                {videosForLang.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
                                        {videosForLang.map((url, i) => (
                                            <YouTubeEmbed key={i} url={url} maxWidth={720} />
                                        ))}
                                    </Box>
                                ) : (current.videoUrls.length > 0 || fallbackVideos.length > 0) ? (
                                    // Has videos in some language — show "no video" note + optional cn fallback
                                    <Box sx={{ mb: 2.5 }}>
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#94A3B8', fontStyle: 'italic', mb: 1 }}>
                                            {NO_VIDEO_TEXT[viewLang]}
                                        </Typography>
                                        {fallbackVideos.map((url, i) => (
                                            <YouTubeEmbed key={i} url={url} maxWidth={720} />
                                        ))}
                                    </Box>
                                ) : null}

                                {pick(current, 'description') && (
                                    <Typography
                                        sx={{
                                            fontFamily: PP, fontSize: '0.86rem', color: '#334155', lineHeight: 1.7,
                                            whiteSpace: 'pre-line',
                                            mb: current.mediaUrls.length ? 2.5 : 0,
                                        }}
                                    >
                                        <Highlight text={pick(current, 'description')} query={search} />
                                    </Typography>
                                )}

                                {current.mediaUrls.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {current.mediaUrls.map((url, i) => {
                                            const src = resolveImageUrl(url);
                                            return (
                                                <a key={i} href={src} target="_blank" rel="noreferrer">
                                                    <img
                                                        src={src} alt=""
                                                        style={{ maxWidth: 320, maxHeight: 220, borderRadius: 8, objectFit: 'cover', border: '1px solid #E2E8F0' }}
                                                    />
                                                </a>
                                            );
                                        })}
                                    </Box>
                                )}

                                {/* Attachments — download links */}
                                {parseAttachments(current.attachments).length > 0 && (
                                    <Box sx={{ mt: current.mediaUrls.length ? 0 : 1.5, mb: 2 }}>
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.75 }}>
                                            Attachments
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {parseAttachments(current.attachments).map((att, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <AttachFileIcon sx={{ fontSize: 15, color: '#64748B', flexShrink: 0 }} />
                                                    <a
                                                        href={resolveImageUrl(att.url)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        download
                                                        style={{ fontFamily: PP, fontSize: '0.82rem', color: '#1A8CFF', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    >
                                                        {att.name}
                                                    </a>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Action bar: Previous | Mark as done toggle | Next */}
                                <Box
                                    sx={{
                                        mt: 3, pt: 2, borderTop: '1px solid #F1F5F9',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
                                    }}
                                >
                                    <Button
                                        onClick={handlePrev} disabled={isFirst}
                                        startIcon={<ChevronLeftIcon />}
                                        sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.85rem', color: '#64748B', '&.Mui-disabled': { color: '#CBD5E1' } }}
                                    >
                                        {viewLang === 'cn' ? '上一个' : viewLang === 'my' ? 'Sebelum' : 'Previous'}
                                    </Button>

                                    {/* Mark as done toggle */}
                                    <Button
                                        onClick={() => current && toggleDone(current.id)}
                                        variant={currentIsDone ? 'contained' : 'outlined'}
                                        startIcon={
                                            currentIsDone
                                                ? <CheckCircleIcon sx={{ fontSize: 16 }} />
                                                : <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />
                                        }
                                        sx={{
                                            fontFamily: PP,
                                            textTransform: 'none',
                                            fontSize: '0.82rem',
                                            fontWeight: 600,
                                            borderRadius: '8px',
                                            boxShadow: 'none',
                                            ...(currentIsDone ? {
                                                bgcolor: '#16A34A',
                                                color: '#fff',
                                                border: 'none',
                                                '&:hover': { bgcolor: '#15803D' },
                                            } : {
                                                color: '#64748B',
                                                borderColor: '#CBD5E1',
                                                '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
                                            }),
                                        }}
                                    >
                                        {currentIsDone ? markLabels.done : markLabels.pending}
                                    </Button>

                                    <Button
                                        onClick={handleNext} variant="contained" disabled={isLast}
                                        endIcon={<ChevronRightIcon />}
                                        sx={{
                                            fontFamily: PP, textTransform: 'none', fontSize: '0.85rem', fontWeight: 600,
                                            borderRadius: '8px', boxShadow: 'none',
                                            background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)',
                                            '&.Mui-disabled': { background: '#E2E8F0', color: '#94A3B8' },
                                        }}
                                    >
                                        {viewLang === 'cn' ? '下一个' : viewLang === 'my' ? 'Seterus' : 'Next'}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            )}

            {/* Edit topic cover */}
            <CoverEditDialog
                open={!!coverEdit}
                category={coverEdit?.category}
                label={coverEdit ? tLabel(coverEdit.category) : ''}
                currentCover={coverEdit ? (categoryCovers[coverEdit.category] || '') : ''}
                onClose={() => setCoverEdit(null)}
                onSaved={async () => { await reloadCategoryCovers(); setCoverEdit(null); }}
            />

            {/* Delete confirmation */}
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs">
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>Delete this tutorial?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                        "{confirmDelete?.title}" will be removed. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmDelete(null)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" color="error"
                        sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function TocRow({ item, number, active, indent, onClick, query, pickTitle, isDone }) {
    const label = pickTitle ? pickTitle(item) : (item.title || '');
    return (
        <Box
            onClick={onClick}
            sx={{
                cursor: 'pointer', py: 0.6, px: 1, pl: indent ? 3.5 : 1.5, borderRadius: '8px',
                bgcolor: active ? '#EFF6FF' : 'transparent',
                '&:hover': { bgcolor: active ? '#EFF6FF' : '#F1F5F9' },
                display: 'flex', alignItems: 'center', gap: 0.75,
            }}
        >
            {/* Status icon: green check if done, red hollow circle if pending */}
            {isDone ? (
                <CheckCircleIcon sx={{ fontSize: 13, color: '#16A34A', flexShrink: 0 }} />
            ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 13, color: '#EF4444', flexShrink: 0 }} />
            )}
            {/* Content title — "1.1.1 How to fill the form" */}
            <Typography
                sx={{
                    fontFamily: PP, fontSize: '0.78rem', fontWeight: active ? 700 : 500,
                    color: active ? '#1D4ED8' : '#64748B',
                    lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
            >
                {number ? `${number} ` : ''}<Highlight text={label} query={query} />
            </Typography>
        </Box>
    );
}

const COVER_HINT = {
    cn: '选择左侧课程开始',
    en: 'Select a lesson on the left to begin',
    my: 'Pilih pelajaran di kiri untuk bermula',
};

function CoverLandingPanel({ coverUrl, label, viewLang, canManage, onEditCover }) {
    const resolved = coverUrl ? resolveImageUrl(coverUrl) : null;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            {resolved ? (
                <Box sx={{ mx: { xs: -2, md: -3 }, mt: { xs: -2, md: -3 }, mb: 3 }}>
                    <img
                        src={resolved}
                        alt=""
                        style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: '12px 12px 0 0',
                            display: 'block',
                        }}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        mx: { xs: -2, md: -3 }, mt: { xs: -2, md: -3 }, mb: 3,
                        bgcolor: '#F1F5F9',
                        borderRadius: '12px 12px 0 0',
                        height: 240,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <MenuBookIcon sx={{ fontSize: 64, color: '#CBD5E1' }} />
                </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Typography sx={{ fontFamily: PP, fontSize: '1.6rem', fontWeight: 800, color: '#0F172A', lineHeight: 1.25 }}>
                    {label}
                </Typography>
                {canManage && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                        onClick={onEditCover}
                        sx={{
                            fontFamily: PP,
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '8px',
                            flexShrink: 0,
                            ml: 1,
                            borderColor: '#CBD5E1',
                            color: '#64748B',
                            '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
                        }}
                    >
                        Edit cover
                    </Button>
                )}
            </Box>
            <Typography sx={{ fontFamily: PP, fontSize: '0.9rem', color: '#94A3B8' }}>
                {COVER_HINT[viewLang] || COVER_HINT.cn}
            </Typography>
        </Box>
    );
}

function CoverEditDialog({ open, category, label, currentCover, onClose, onSaved }) {
    const [coverUrl, setCoverUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    // Sync local state when the dialog opens for a category
    useEffect(() => {
        if (open) setCoverUrl(currentCover || '');
    }, [open, currentCover]);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (fileRef.current) fileRef.current.value = '';
        if (!file) return;
        setUploading(true);
        try {
            const r = await uploadTutorialImage(file);
            if (r?.data?.url) setCoverUrl(r.data.url);
        } catch {
            alert('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!category) return;
        setSaving(true);
        try {
            await setCategoryCover(category, coverUrl || '');
            await onSaved();
        } catch {
            alert('Failed to save cover');
        } finally {
            setSaving(false);
        }
    };

    const resolved = coverUrl ? resolveImageUrl(coverUrl) : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>
                Edit cover — {label}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.5, pt: 0.5 }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#64748B' }}>
                        Recommended size: 1600 × 400 px (wide banner — shown full width, ~240px tall, center-cropped).
                    </Typography>
                    {resolved ? (
                        <img
                            src={resolved}
                            alt=""
                            style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8, border: '1px solid #E2E8F0', display: 'block' }}
                        />
                    ) : (
                        <Box sx={{ width: '100%', height: 200, bgcolor: '#F1F5F9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MenuBookIcon sx={{ fontSize: 56, color: '#CBD5E1' }} />
                        </Box>
                    )}

                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFile}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={uploading}
                            onClick={() => fileRef.current?.click()}
                            sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}
                        >
                            {uploading ? 'Uploading...' : (coverUrl ? 'Replace photo' : 'Upload photo')}
                        </Button>
                        {coverUrl && (
                            <Button
                                size="small"
                                color="error"
                                disabled={uploading}
                                onClick={() => setCoverUrl('')}
                                sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}
                            >
                                Remove
                            </Button>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || uploading}
                    sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)' }}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function DonePanel({ total, onRestart }) {
    return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: '#10B981', mb: 1 }} />
            <Typography sx={{ fontFamily: PP, fontSize: '1.25rem', fontWeight: 700, color: '#0F172A' }}>
                You've completed all {total} topics.
            </Typography>
            <Typography sx={{ fontFamily: PP, fontSize: '0.9rem', color: '#64748B', mt: 1, mb: 3 }}>
                Nice work. Come back any time — your team needs sharp agents.
            </Typography>
            <Button onClick={onRestart} variant="outlined"
                sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
            >
                Start over
            </Button>
        </Box>
    );
}
